import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cookies must ride along with every request for the session to work, and
// a 401 from ANY endpoint (every /api/* route except /auth/me requires a
// session - see backend/auth_service.py's auth_middleware) always means
// "not signed in" in this app, so sending the user straight to Microsoft
// login is the right global behavior rather than a dead-end error toast.
axios.defaults.withCredentials = true;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = `${API}/auth/login`;
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Every page in the app's nav that IT can independently grant/revoke on the
// Access Management page - kept in sync with backend/auth_service.py's
// PAGE_CATALOG (labels only live here; the source of truth for WHICH pages
// exist and what backend routes they gate is the backend).
export const PAGE_LABELS = {
  bom_explorer: "BOM Management",
  purchasing_plan: "Procurement Planning",
  production_plan: "Production Planning",
  inventory: "Stock Overview",
  // Aug 27 2026, user's explicit ask: split out of "inventory" into its
  // own grantable right.
  stock_transfer: "Inter Plant Stock Transfer",
  // Sep 3 2026, user's explicit ask: split out of "anyUser" into its own
  // grantable right (was previously open to any logged-in user). Feb
  // 2026, renamed "Inbound Receipt" (2nd revision) - a single grant now
  // covers EITHER destination-warehouse scope ("RM & Other" or
  // "SFG/FG", picked separately on the Access Management page) instead
  // of implying FG/SFG only.
  inbound_stock_transfer: "Inbound Receipt",
  supplier_master: "Supplier Master",
  supplier_onboarding_approval: "New Supplier Approval",
  quota_allocation: "Quota Allocation",
  admin: "Component Master (Admin)",
  admin_sap_write: "SAP Write (Admin)",
  admin_create_material: "Create Material (Admin)",
  admin_activate_material_site: "Activate Material Site (Admin)",
  store_approval: "Store Goods Issue",
  supplier_portal_admin: "Supplier Portal Approvals",
  purchase_order: "Purchase Order Creation",
  // Sep 5 2026, user's explicit ask: split out of the "supplier_portal_admin"/
  // "purchase_order" catch-alls above into their own grantable rights.
  supplier_portal_invite: "Invite Supplier",
  vendor_goods_receipt: "Vendor Goods Receipt",
  supplier_dashboard: "Supplier Dashboard",
  created_purchase_orders: "Created POs",
  open_purchase_orders: "Open Purchase Orders",
  // Sep 21 2026, user's explicit ask (real incident, PO 25271): Cancel PO/
  // Cancel Item is now its own grantable right, separate from just being
  // able to view Open Purchase Orders.
  po_cancel: "Cancel Purchase Order",
  // Sep 9 2026, user's explicit ask: separate view-only right for the
  // GST/PAN/MSME/Bank documents on the Supplier Portal Approvals page,
  // without the Approve/Reject actions (still gated behind
  // supplier_portal_admin only).
  supplier_portal_documents: "Supplier Additional Documents",
  // Sep 18 2026, user's explicit ask: lets staff create a shipment on
  // behalf of a supplier + reset a supplier's password.
  act_as_supplier: "Act as Supplier",
  // Sep 22 2026, user's explicit ask: at-a-glance SAP Event Notification
  // webhook health (counts per Business Object + last-received time).
  admin_webhook_health: "Webhook Health (Admin)",
  // Sep 25 2026, user's explicit ask: at-a-glance last-refresh time/status
  // for every SAP master-data cache.
  data_sync_status: "Data Sync Status (Admin)",
  // Oct 2026, user's explicit ask: SAP-GUI-style Document Flow search
  // (any doc ID -> its full chain of related documents + statuses).
  document_flow: "Document Flow",
  // Oct 7 2026, user's explicit ask: "create separate purchase access
  // right for this" - deliberately its own grantable right, separate
  // from "purchase_order".
  interplant_purchase_order: "Interplant Purchase Order",
  // Oct 13 2026, user's explicit ask: "create separate access right for
  // the service purchase order so admin can grant access to other
  // user" - independently grantable without the broader "purchase_order"
  // right (existing purchase_order holders keep Service PO access too).
  service_purchase_order: "Service Purchase Order",
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.authenticated ? data : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = () => {
    window.location.href = `${API}/auth/login`;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
    window.location.href = "/";
  };

  // Sep 9 2026: also accepts an array of page keys - access is granted if
  // the user has ANY one of them (e.g. Supplier Portal Approvals is
  // reachable with EITHER supplier_portal_admin OR the newer
  // view-only supplier_portal_documents right).
  const hasPageAccess = (pageKey) =>
    !!user && (user.role === "super_admin" || (Array.isArray(pageKey)
      ? pageKey.some((k) => (user.allowed_pages || []).includes(k))
      : (user.allowed_pages || []).includes(pageKey)));

  const isPendingAccess = !!user && user.role !== "super_admin" && (user.allowed_pages || []).length === 0;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, hasPageAccess, isPendingAccess }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
